/**
 * Default label styling per OpenAPI FormItem spec.
 * @type {Pick<FormItemBase, 'italic' | 'bold' | 'color'>}
 */
export const DEFAULT_FORM_ITEM_LABEL_STYLE = {
  italic: false,
  bold: false,
  color: null,
};

/**
 * Empty FormItem shell with all variants set to null.
 * @returns {FormItem}
 */
export function createEmptyFormItem() {
  return {
    text: null,
    textarea: null,
    select: null,
    selectClass: null,
    selectMultiple: null,
    selectMultipleClass: null,
    file: null,
    color: null,
    date: null,
    time: null,
    boolean: null,
    mergeTable: null,
    entity: null,
    checkbox: null,
    checkboxList: null,
    radioList: null,
    radio: null,
    grid: null,
  };
}

/**
 * @param {Partial<FormItemText>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemText(overrides = {}) {
  /** @type {FormItemText} */
  const text = {
    id: 'text_field',
    type: 'text',
    lib: 'Texte',
    align: 'left',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: null,
    default: '',
    width: '',
    value: '',
    liveSearchEnabled: false,
    suggestionsUrl: null,
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), text };
}

/**
 * Text field with styled label (italic + bold + color) for demos.
 * @returns {FormItem}
 */
export function createMockFormItemTextStyledLabel() {
  return createMockFormItemText({
    id: 'styled_label_field',
    lib: 'Label en rouge gras italique',
    italic: true,
    bold: true,
    color: '#dc2626',
  });
}

/**
 * @param {Partial<FormItemTextarea>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemTextarea(overrides = {}) {
  /** @type {FormItemTextarea} */
  const textarea = {
    id: 'textarea_field',
    type: 'text',
    lib: 'Message',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: null,
    default: '',
    width: '',
    value: '',
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), textarea };
}

/**
 * @param {Partial<FormItemColor>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemColor(overrides = {}) {
  /** @type {FormItemColor} */
  const color = {
    id: 'color_field',
    lib: 'Couleur',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: null,
    width: '',
    value: '#FFFFFF',
    default: '#FFFFFF',
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), color };
}

/**
 * @param {Partial<FormItemTime>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemTime(overrides = {}) {
  /** @type {FormItemTime} */
  const time = {
    id: 'time_field',
    lib: 'Heure',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: null,
    width: '',
    value: '09:00:00',
    default: '',
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), time };
}

/**
 * @param {Partial<FormItemEntity>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemEntity(overrides = {}) {
  /** @type {FormItemEntity} */
  const entity = {
    id: 'entity_field',
    lib: 'Entité',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    values: [],
    displayedValues: [],
    multiple: false,
    baseFormUrl: '/form/contact/new?context=lieu',
    default: '',
    help: null,
    width: '',
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), entity };
}

/**
 * @param {Partial<CheckboxListCell>} [overrides]
 * @returns {CheckboxListCell}
 */
function checkboxListCell(overrides = {}) {
  return {
    icon: null,
    imageUrl: null,
    lib: null,
    iconColor: null,
    backgroundColor: null,
    openTabUrl: null,
    ...overrides,
  };
}

/** Default groups for checkboxList demos */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroups = [
  {
    lib: 'Styles (wrap)',
    icon: 'fal fa-tags',
    iconColor: '#2563eb',
    backgroundColor: '#eff6ff',
    layout: 'wrap',
    options: [
      {
        id: 'rock',
        cells: [
          {
            icon: 'fal fa-guitar',
            imageUrl: null,
            lib: '**Rock**',
            iconColor: '#dc2626',
            backgroundColor: '#fef2f2',
            openTabUrl: null,
          },
        ],
      },
      {
        id: 'jazz',
        cells: [
          {
            icon: 'fal fa-music',
            imageUrl: null,
            lib: 'Jazz',
            iconColor: '#7c3aed',
            backgroundColor: '#f5f3ff',
            openTabUrl: null,
          },
        ],
      },
      {
        id: 'electro',
        cells: [
          {
            icon: null,
            imageUrl: 'https://placehold.co/24x24/png',
            lib: 'Électro',
            iconColor: null,
            backgroundColor: null,
            openTabUrl: 'https://example.com/electro',
          },
        ],
      },
    ],
  },
  {
    lib: 'Équipements (column)',
    icon: 'fal fa-toolbox',
    iconColor: '#059669',
    backgroundColor: '#ecfdf5',
    layout: 'column',
    options: [
      {
        id: 'son',
        cells: [
          {
            icon: 'fal fa-volume-up',
            imageUrl: null,
            lib: 'Son',
            iconColor: '#059669',
            backgroundColor: null,
            openTabUrl: null,
          },
          {
            icon: null,
            imageUrl: null,
            lib: '*(PA)*',
            iconColor: null,
            backgroundColor: '#ecfdf5',
            openTabUrl: null,
          },
        ],
      },
      {
        id: 'lumiere',
        cells: [
          {
            icon: 'fal fa-lightbulb',
            imageUrl: null,
            lib: 'Lumière',
            iconColor: '#d97706',
            backgroundColor: null,
            openTabUrl: null,
          },
        ],
      },
    ],
  },
];

/** Wrap layout with many simple options (10) */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsWrapMany = [
  {
    lib: 'Prestations (wrap, 10 options)',
    icon: 'fal fa-concierge-bell',
    iconColor: '#6366f1',
    backgroundColor: '#eef2ff',
    layout: 'wrap',
    options: [
      { id: 'bar', cells: [checkboxListCell({ icon: 'fal fa-glass-martini', lib: 'Bar' })] },
      { id: 'vestiaire', cells: [checkboxListCell({ icon: 'fal fa-tshirt', lib: 'Vestiaire' })] },
      { id: 'catering', cells: [checkboxListCell({ icon: 'fal fa-utensils', lib: 'Catering' })] },
      { id: 'wifi', cells: [checkboxListCell({ icon: 'fal fa-wifi', lib: 'Wi-Fi' })] },
      { id: 'parking', cells: [checkboxListCell({ icon: 'fal fa-parking', lib: 'Parking' })] },
      { id: 'garderie', cells: [checkboxListCell({ lib: 'Garderie' })] },
      { id: 'merch', cells: [checkboxListCell({ lib: 'Merch' })] },
      { id: 'billeterie', cells: [checkboxListCell({ lib: 'Billetterie' })] },
      { id: 'accueil', cells: [checkboxListCell({ lib: 'Accueil' })] },
      { id: 'secu', cells: [checkboxListCell({ icon: 'fal fa-shield-alt', lib: 'Sécurité' })] },
    ],
  },
];

/** Column layout with multi-cell option labels */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsColumnAmenagements = [
  {
    lib: 'Aménagements (column, 5 options)',
    icon: 'fal fa-universal-access',
    iconColor: '#0d9488',
    backgroundColor: '#f0fdfa',
    layout: 'column',
    options: [
      {
        id: 'pmr',
        cells: [
          checkboxListCell({ icon: 'fal fa-wheelchair', lib: 'Accès PMR', iconColor: '#0d9488' }),
          checkboxListCell({ lib: '*(recommandé)*', backgroundColor: '#f0fdfa' }),
        ],
      },
      {
        id: 'ascenseur',
        cells: [
          checkboxListCell({ icon: 'fal fa-elevator', lib: 'Ascenseur' }),
        ],
      },
      {
        id: 'toilettes',
        cells: [
          checkboxListCell({ icon: 'fal fa-restroom', lib: 'Toilettes adaptées' }),
        ],
      },
      {
        id: 'ramp',
        cells: [
          checkboxListCell({ lib: 'Rampe d\'accès' }),
          checkboxListCell({ icon: 'fal fa-angle-up', iconColor: '#64748b' }),
        ],
      },
      {
        id: 'places',
        cells: [
          checkboxListCell({ lib: '**Places** réservées', iconColor: '#0d9488' }),
        ],
      },
    ],
  },
];

/** Headers for column table layout (applied to column groups only; ignored for wrap) */
/** @type {CheckboxListHeader[]} */
export const mockCheckboxListHeadersAmenagements = [
  { lib: 'Équipement', width: '40%' },
  { lib: 'Détail', width: '60%' },
];

/** Column group + headers for table layout demos */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsColumnWithHeaders = mockCheckboxListGroupsColumnAmenagements;

/** Deux groupes labellisés (wrap + column) dans un même champ — démo multi-groupes */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsPrestationsAndAmenagements = [
  ...mockCheckboxListGroupsWrapMany,
  ...mockCheckboxListGroupsColumnAmenagements,
];

/** Rich cells: icons, images, markdown, links, colors */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsRichCanaux = [
  {
    lib: 'Canaux (wrap, contenu riche)',
    icon: 'fal fa-bullhorn',
    iconColor: '#db2777',
    backgroundColor: '#fdf2f8',
    layout: 'wrap',
    options: [
      {
        id: 'site',
        cells: [
          checkboxListCell({
            icon: 'fal fa-globe',
            lib: 'Site web',
            iconColor: '#2563eb',
            openTabUrl: 'https://example.com',
          }),
        ],
      },
      {
        id: 'facebook',
        cells: [
          checkboxListCell({
            imageUrl: 'https://placehold.co/20x20/1877f2/fff?text=f',
            lib: '**Facebook**',
          }),
        ],
      },
      {
        id: 'instagram',
        cells: [
          checkboxListCell({
            imageUrl: 'https://placehold.co/20x20/e4405f/fff?text=ig',
            lib: 'Instagram',
            openTabUrl: 'https://instagram.com',
          }),
        ],
      },
      {
        id: 'newsletter',
        cells: [
          checkboxListCell({
            icon: 'fal fa-envelope',
            lib: 'Newsletter',
            backgroundColor: '#fef3c7',
            iconColor: '#d97706',
          }),
        ],
      },
      {
        id: 'affiche',
        cells: [
          checkboxListCell({ lib: 'Affichage *urbain*' }),
        ],
      },
      {
        id: 'radio',
        cells: [
          checkboxListCell({
            icon: 'fal fa-broadcast-tower',
            lib: 'Radio',
            iconColor: '#7c3aed',
          }),
          checkboxListCell({ lib: 'FM', backgroundColor: '#f5f3ff' }),
        ],
      },
    ],
  },
];

/** Single option edge case */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsSingleOption = [
  {
    lib: 'Option unique (column)',
    icon: 'fal fa-star',
    iconColor: '#ca8a04',
    backgroundColor: '#fefce8',
    layout: 'column',
    options: [
      {
        id: 'featured',
        cells: [
          checkboxListCell({
            icon: 'fal fa-star',
            lib: 'Lieu mis en avant',
            iconColor: '#ca8a04',
          }),
        ],
      },
    ],
  },
];

/** Group without legend header */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsNoHeader = [
  {
    lib: null,
    icon: null,
    iconColor: null,
    backgroundColor: null,
    layout: 'wrap',
    options: [
      { id: 'matin', cells: [checkboxListCell({ lib: 'Matin' })] },
      { id: 'aprem', cells: [checkboxListCell({ lib: 'Après-midi' })] },
      { id: 'soir', cells: [checkboxListCell({ lib: 'Soir' })] },
      { id: 'nuit', cells: [checkboxListCell({ lib: 'Nuit' })] },
    ],
  },
];

/** Two groups with different layouts in one field */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsMixedLayouts = [
  {
    lib: 'Formats courts (wrap)',
    icon: 'fal fa-clock',
    iconColor: '#475569',
    backgroundColor: '#f8fafc',
    layout: 'wrap',
    options: [
      { id: 'concert', cells: [checkboxListCell({ lib: 'Concert' })] },
      { id: 'dj', cells: [checkboxListCell({ lib: 'DJ set' })] },
      { id: 'theatre', cells: [checkboxListCell({ lib: 'Théâtre' })] },
    ],
  },
  {
    lib: 'Formats longs (column)',
    icon: 'fal fa-film',
    iconColor: '#475569',
    backgroundColor: '#f8fafc',
    layout: 'column',
    options: [
      { id: 'residence', cells: [checkboxListCell({ lib: 'Résidence artistique' })] },
      { id: 'festival', cells: [checkboxListCell({ lib: 'Festival multi-jours' })] },
      { id: 'marathon', cells: [checkboxListCell({ lib: 'Marathon culturel' })] },
      { id: 'biennale', cells: [checkboxListCell({ lib: 'Biennale' })] },
    ],
  },
];

/** Minimal group for required-field demo */
/** @type {CheckboxListGroup[]} */
export const mockCheckboxListGroupsRequired = [
  {
    lib: 'Au moins une option requise',
    icon: 'fal fa-exclamation-circle',
    iconColor: '#dc2626',
    backgroundColor: '#fef2f2',
    layout: 'wrap',
    options: [
      { id: 'interne', cells: [checkboxListCell({ lib: 'Usage interne' })] },
      { id: 'public', cells: [checkboxListCell({ lib: 'Usage public' })] },
      { id: 'partenaire', cells: [checkboxListCell({ lib: 'Partenaires' })] },
    ],
  },
];

/**
 * @param {Partial<FormItemCheckboxList>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemCheckboxList(overrides = {}) {
  /** @type {FormItemCheckboxList} */
  const checkboxList = {
    id: 'checkbox_list_demo',
    values: ['rock'],
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: 'Sélectionnez les styles et équipements.',
    default: ['rock'],
    headers: [],
    groups: mockCheckboxListGroups,
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), checkboxList };
}

/**
 * @param {Partial<FormItemRadioList>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemRadioList(overrides = {}) {
  /** @type {FormItemRadioList} */
  const radioList = {
    id: 'radio_list_demo',
    lib: 'Mode',
    value: 'rock',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: 'Choisissez une seule option.',
    default: 'rock',
    headers: [],
    groups: mockCheckboxListGroups,
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), radioList };
}

/**
 * @param {Partial<FormItemCheckbox>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemCheckbox(overrides = {}) {
  /** @type {FormItemCheckbox} */
  const checkbox = {
    id: 'checkbox_field',
    lib: 'Accepter',
    ...DEFAULT_FORM_ITEM_LABEL_STYLE,
    help: null,
    checked: false,
    value: null,
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), checkbox };
}

/**
 * @param {Partial<FormItemGrid>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemGrid(overrides = {}) {
  const nestedText = createMockFormItemText({
    id: 'grid_cell_name',
    lib: 'Nom',
    value: 'Alice',
    default: '',
  });
  const nestedCheckbox = createMockFormItemCheckbox({
    id: 'grid_cell_active',
    lib: 'Actif',
    checked: true,
    value: 'on',
  });

  /** @type {FormTableData} */
  const defaultTable = {
    headers: [
      {
        colId: 'label', value: 'Libellé', width: '120px', align: 'left', selectable: false, backgroundColor: null,
      },
      {
        colId: 'input', value: 'Valeur', width: '200px', align: 'left', selectable: false, backgroundColor: null,
      },
    ],
    groups: [
      {
        lib: '',
        rows: [
          {
            cells: [
              {
                text: {
                  rowId: 'r1',
                  colId: 'label',
                  type: 'text',
                  link: null,
                  backgroundColor: null,
                  value: 'Nom',
                },
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: null,
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: {
                  rowId: 'r1',
                  colId: 'input',
                  formItem: nestedText,
                },
              },
            ],
          },
          {
            cells: [
              {
                text: {
                  rowId: 'r2',
                  colId: 'label',
                  type: 'text',
                  link: null,
                  backgroundColor: null,
                  value: 'Actif',
                },
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: null,
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: {
                  rowId: 'r2',
                  colId: 'input',
                  formItem: nestedCheckbox,
                },
              },
            ],
          },
        ],
      },
    ],
    footers: [],
  };

  /** @type {FormItemGrid} */
  const grid = {
    id: 'grid_demo',
    lib: 'Grille',
    table: defaultTable,
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), grid };
}

/**
 * @param {Partial<FormItemRadio>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemRadio(overrides = {}) {
  /** @type {FormItemRadio} */
  const radio = {
    id: 'radio_demo',
    lib: 'Option',
    checked: false,
    value: 'option',
    validation: { required: false },
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), radio };
}

/**
 * One exclusive radio group: every option shares `id`, only `value` differs.
 * @param {object} params
 * @param {string} params.id - Shared group id, posted once with the selected value
 * @param {Array<{ value: string, lib: string }>} params.options
 * @param {string} [params.selected] - Value of the option checked by default
 * @param {boolean} [params.required]
 * @returns {FormItem[]}
 */
export function createMockRadioGroup({
  id, options, selected = '', required = false,
}) {
  return options.map(({ value, lib }) => createMockFormItemRadio({
    id,
    lib,
    value,
    checked: value === selected,
    validation: { required },
  }));
}

/** Right levels offered for each module, one column per level */
const RIGHTS_COLUMNS = [
  { colId: 'default', lib: 'Par défaut', backgroundColor: '#eaf5ec' },
  { colId: 'readonly', lib: 'Lecture seule', backgroundColor: '#fdf3e3' },
  { colId: 'hidden', lib: 'Masqué', backgroundColor: '#fbeaea' },
];

/**
 * Matrix row: the module label plus one radio option per right level.
 * All options of the row share the row id, so the row posts a single value.
 * @param {object} params
 * @param {string} params.id - Row id, also the radio group id
 * @param {string} params.lib - Module label
 * @param {string} params.selected - colId of the checked level
 * @returns {FormTableRow}
 */
function createRightsMatrixRow({ id, lib, selected }) {
  /** @type {FormTableCell} */
  const labelCell = {
    text: {
      rowId: id,
      colId: 'module',
      type: 'text',
      link: null,
      backgroundColor: '#f7f7f9',
      value: lib,
    },
    markdown: null,
    boolean: null,
    icon: null,
    image: null,
    field: null,
  };

  return {
    cells: [
      labelCell,
      ...RIGHTS_COLUMNS.map(({ colId }) => ({
        text: null,
        markdown: null,
        boolean: null,
        icon: null,
        image: null,
        field: {
          rowId: id,
          colId,
          // The column header carries the meaning, so the option has no label
          formItem: createMockFormItemRadio({
            id, lib: '', value: colId, checked: selected === colId,
          }),
        },
      })),
    ],
  };
}

/**
 * Rights matrix grid: radio groups inside a grid, coloured headers and grouped
 * rows. Ids are bracketed on purpose to exercise the RHF field name codec.
 * @param {Partial<FormItemGrid>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemGridRights(overrides = {}) {
  /** @type {FormTableData} */
  const table = {
    headers: [
      {
        colId: 'module', value: 'Module', width: '260px', align: 'left', selectable: false, backgroundColor: '#f7f7f9',
      },
      ...RIGHTS_COLUMNS.map(({ colId, lib, backgroundColor }) => ({
        colId, value: lib, width: '140px', align: 'center', selectable: false, backgroundColor,
      })),
    ],
    groups: [
      {
        lib: 'Administration',
        rows: [
          createRightsMatrixRow({ id: 'user_custom_module_crediential[7425]', lib: 'Utilisateurs', selected: 'default' }),
          createRightsMatrixRow({ id: 'user_custom_module_crediential[7426]', lib: 'Rôles et droits', selected: 'readonly' }),
          createRightsMatrixRow({ id: 'user_custom_module_crediential[7427]', lib: 'Paramètres généraux', selected: 'hidden' }),
        ],
      },
      {
        lib: 'Exploitation',
        rows: [
          createRightsMatrixRow({ id: 'user_custom_module_crediential[8001]', lib: 'Planning', selected: 'default' }),
          createRightsMatrixRow({ id: 'user_custom_module_crediential[8002]', lib: 'Facturation', selected: 'readonly' }),
        ],
      },
    ],
    footers: [
      { value: 'Chaque ligne envoie une seule valeur, sous son id complet (ex. `user_custom_module_crediential[7425]`).', align: 'left' },
    ],
  };

  /** @type {FormItemGrid} */
  const grid = {
    id: 'grid_droits_modules',
    lib: 'Droits par module',
    table,
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), grid };
}

/**
 * Invoice-style grid with computed PU/total, tag aggregate and subRow discount.
 * @param {Partial<FormItemGrid>} [overrides]
 * @returns {FormItem}
 */
export function createMockFormItemGridInvoice(overrides = {}) {
  const rowId = 'invoice_row[3]';
  const puId = 'invoice_row_pu_ht[3]';
  const qtyId = 'invoice_row_amount[3]';
  const totalId = 'invoice_row_tp_ht[3]';

  const puField = createMockFormItemText({
    id: puId,
    type: 'float',
    lib: '',
    value: '10',
    default: '10',
    readOnly: true,
    computedValue: [{
      when: [],
      expression: {
        operation: {
          op: 'div',
          args: [
            { fieldRef: totalId },
            { fieldRef: qtyId },
          ],
        },
      },
      onDivideByZero: 'skip',
      round: { digits: 5 },
    }],
  });

  const qtyField = createMockFormItemText({
    id: qtyId,
    type: 'float',
    lib: '',
    value: '2',
    default: '2',
  });

  const totalField = createMockFormItemText({
    id: totalId,
    type: 'float',
    lib: '',
    value: '20',
    default: '20',
    readOnly: true,
    tags: ['row_total_ht'],
    computedValue: [{
      when: [],
      expression: {
        operation: {
          op: 'mul',
          args: [
            { fieldRef: puId },
            { fieldRef: qtyId },
          ],
        },
      },
      round: { digits: 2 },
    }],
  });

  const grandTotalField = createMockFormItemText({
    id: 'invoice_grand_total_ht',
    type: 'float',
    lib: 'Total HT',
    value: '20',
    default: '20',
    readOnly: true,
    computedValue: [{
      when: [],
      expression: {
        operation: {
          op: 'sum',
          args: [{ tagRef: { tag: 'row_total_ht', filter: null } }],
        },
      },
      round: { digits: 2 },
    }],
  });

  /** @type {FormTableData} */
  const table = {
    headers: [
      {
        colId: 'label', value: '', width: '120px', align: 'left', selectable: false, backgroundColor: null,
      },
      {
        colId: 'pu', value: 'PU HT', width: '100px', align: 'right', selectable: false, backgroundColor: null,
      },
      {
        colId: 'qty', value: 'Qté', width: '80px', align: 'right', selectable: false, backgroundColor: null,
      },
      {
        colId: 'total', value: 'Total HT', width: '100px', align: 'right', selectable: false, backgroundColor: null,
      },
    ],
    groups: [
      {
        lib: '',
        rows: [
          {
            rowId,
            cells: [
              {
                text: {
                  rowId,
                  colId: 'label',
                  type: 'text',
                  link: null,
                  backgroundColor: null,
                  value: 'Ligne 3',
                },
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: null,
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: { rowId, colId: 'pu', formItem: puField },
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: { rowId, colId: 'qty', formItem: qtyField },
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: { rowId, colId: 'total', formItem: totalField },
              },
            ],
            subRows: [
              {
                rowId: 'invoice_row_discount_zone[3]',
                hideConditions: [{ id: qtyId, operator: 'isEmpty', values: [] }],
                cells: [
                  {
                    text: {
                      rowId: 'invoice_row_discount_zone[3]',
                      colId: 'label',
                      type: 'text',
                      link: null,
                      backgroundColor: null,
                      value: 'Remise',
                    },
                    markdown: null,
                    boolean: null,
                    icon: null,
                    image: null,
                    field: null,
                  },
                  {
                    text: null,
                    markdown: null,
                    boolean: null,
                    icon: null,
                    image: null,
                    field: {
                      rowId: 'invoice_row_discount_zone[3]',
                      colId: 'pu',
                      formItem: createMockFormItemText({
                        id: 'invoice_row_discount[3]',
                        lib: '',
                        value: '',
                        default: '',
                      }),
                    },
                  },
                  {
                    text: null, markdown: null, boolean: null, icon: null, image: null, field: null,
                  },
                  {
                    text: null, markdown: null, boolean: null, icon: null, image: null, field: null,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        lib: '',
        rows: [
          {
            rowId: 'invoice_footer',
            cells: [
              {
                text: null, markdown: null, boolean: null, icon: null, image: null, field: null,
              },
              {
                text: null, markdown: null, boolean: null, icon: null, image: null, field: null,
              },
              {
                text: null, markdown: null, boolean: null, icon: null, image: null, field: null,
              },
              {
                text: null,
                markdown: null,
                boolean: null,
                icon: null,
                image: null,
                field: { rowId: 'invoice_footer', colId: 'total', formItem: grandTotalField },
              },
            ],
          },
        ],
      },
    ],
    footers: [],
  };

  /** @type {FormItemGrid} */
  const grid = {
    id: 'grid_invoice_demo',
    lib: 'Ligne de facture (computedValue)',
    table,
    hideConditions: [],
    ...overrides,
  };
  return { ...createEmptyFormItem(), grid };
}
